import { logger } from '@/utils/screenEventsLogger';
import { SCREEN_ID_ENUM, TIZEN_LOCAL_FILE_KEY } from '@/constants';
import { apiGetScreenLayoutMediaAsset } from '@/api/layouts';
import { getCloudfrontUrl } from '@/helpers/utils';

export function getTizenVideoItemKey(itemId) {
  return `${TIZEN_LOCAL_FILE_KEY}-${itemId}`;
}

export function removeTizenDownloads() {
  return new Promise((resolve, reject) => {
    try {
      logger.addLog('Starting removal of Tizen downloaded videos and files');

      // Check if Tizen filesystem API is available
      if (typeof tizen === 'undefined' || !tizen.filesystem) {
        logger.addLog('Error: Tizen filesystem API not available');
        reject(new Error('Tizen filesystem API not available'));
        return;
      }

      let removedFiles = 0;
      let clearedLocalStorageKeys = 0;
      let errors = [];

      // Get all localStorage keys that match our pattern
      const keysToRemove = [];
      const filePaths = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(TIZEN_LOCAL_FILE_KEY)) {
          const storedPath = localStorage.getItem(key);
          if (storedPath) {
            keysToRemove.push(key);
            filePaths.push(storedPath);
          }
        }
      }

      logger.addLog(`Found ${keysToRemove.length} files to remove`);

      if (keysToRemove.length === 0) {
        logger.addLog('No Tizen video files to remove');
        resolve({ removedFiles: 0, clearedLocalStorageKeys: 0, errors: [] });
        return;
      }

      let processedCount = 0;
      const totalFiles = filePaths.length;

      const checkAllProcessed = () => {
        if (processedCount === totalFiles) {
          const result = {
            removedFiles,
            clearedLocalStorageKeys,
            errors,
            message: `Processed ${totalFiles} files: ${removedFiles} removed, ${clearedLocalStorageKeys} localStorage keys cleared, ${errors.length} errors`,
          };
          logger.addLog(result.message);

          if (errors.length > 0) {
            logger.addLog(`Errors encountered: ${errors.join(', ')}`);
          }
          logger
            .sendLogs()
            .then(() => {
              resolve(result);
            })
            .catch(() => {
              resolve(result);
            });
        }
      };

      // Process each file
      filePaths.forEach((filePath, index) => {
        const key = keysToRemove[index];

        try {
          // Use the Tizen filesystem resolve method to get a file handle
          tizen.filesystem.resolve(
            filePath,
            // Success callback
            (file) => {
              try {
                // Delete the file
                file.parent.deleteFile(
                  file.fullPath,
                  // Delete success callback
                  () => {
                    logger.addLog(`Successfully removed file: ${filePath}`);
                    removedFiles++;

                    // Remove from localStorage
                    localStorage.removeItem(key);
                    clearedLocalStorageKeys++;
                    logger.addLog(`Cleared localStorage key: ${key}`);

                    processedCount++;
                    checkAllProcessed();
                  },
                  // Delete error callback
                  (error) => {
                    logger.addLog(`Error deleting file ${filePath}: ${error.message}`);
                    errors.push(`Delete ${filePath}: ${error.message}`);

                    // Still remove from localStorage even if file deletion failed
                    localStorage.removeItem(key);
                    clearedLocalStorageKeys++;

                    processedCount++;
                    checkAllProcessed();
                  },
                );
              } catch (deleteError) {
                logger.addLog(`Error in delete operation for ${filePath}: ${deleteError.message}`);
                errors.push(`Delete operation ${filePath}: ${deleteError.message}`);

                // Still remove from localStorage
                localStorage.removeItem(key);
                clearedLocalStorageKeys++;

                processedCount++;
                checkAllProcessed();
              }
            },
            // Resolve error callback
            (error) => {
              logger.addLog(`Error resolving file ${filePath}: ${error.message}`);
              errors.push(`Resolve ${filePath}: ${error.message}`);

              // Still remove from localStorage even if file resolve failed
              localStorage.removeItem(key);
              clearedLocalStorageKeys++;

              processedCount++;
              checkAllProcessed();
            },
          );
        } catch (resolveError) {
          logger.addLog(`Error starting resolve for ${filePath}: ${resolveError.message}`);
          errors.push(`Start resolve ${filePath}: ${resolveError.message}`);

          // Still remove from localStorage
          localStorage.removeItem(key);
          clearedLocalStorageKeys++;

          processedCount++;
          checkAllProcessed();
        }
      });
    } catch (error) {
      logger.addLog(`Error during Tizen downloads removal: ${error.message}`);
      reject(error);
    }
  });
}

function getFileExtensionFromUrl(url) {
  try {
    const cleanUrl = url.split('?')[0].split('#')[0];
    const filename = cleanUrl.split('/').pop();
    const extension = filename.includes('.') ? filename.split('.').pop().toLowerCase() : 'mp4';
    return extension;
  } catch (error) {
    logger.addLog(`Error extracting extension from URL: ${error.message}`);
    return 'mp4';
  }
}

function isAlreadyDownloaded(fileId) {
  return new Promise((resolve) => {
    try {
      const localStorageKey = getTizenVideoItemKey(fileId);
      const storedPath = localStorage.getItem(localStorageKey);

      // If no localStorage entry, file is not downloaded
      if (!storedPath) {
        resolve(false);
        return;
      }

      // Extract filename from the full path (e.g., picflow_xxx.mp4)
      const fileName = storedPath.split('/').pop();

      // Check if Tizen filesystem API is available
      if (typeof tizen === 'undefined' || !tizen.filesystem) {
        logger.addLog('Tizen filesystem API not available, relying on localStorage');
        resolve(true);
        return;
      }

      // Check if file actually exists in documents folder
      tizen.filesystem.resolve(
        'documents',
        (dir) => {
          dir.listFiles(
            (files) => {
              const fileExists = files.some((file) => file.name === fileName);
              if (fileExists) {
                logger.addLog(`File ${fileId} already downloaded at: ${storedPath}`);
                resolve(true);
              } else {
                logger.addLog(`File ${fileId} not found in documents, removing localStorage entry`);
                localStorage.removeItem(localStorageKey);
                resolve(false);
              }
            },
            (err) => {
              logger.addLog(`Error listing files: ${err.message}, relying on localStorage`);
              resolve(true);
            },
          );
        },
        (e) => {
          logger.addLog(`Error resolving documents folder: ${e.message}, relying on localStorage`);
          resolve(true);
        },
        'r',
      );
    } catch (error) {
      logger.addLog(`Error checking if file exists: ${error.message}`);
      resolve(false);
    }
  });
}

export async function downloadMediaForTizen() {
  return new Promise((resolve, reject) => {
    const screenId = localStorage.getItem(SCREEN_ID_ENUM);
    apiGetScreenLayoutMediaAsset(screenId)
      .then(async (data) => {
        const mediaAssets = data.mediaAssets.filter((asset) => asset.fontName === null);

        // Check each asset asynchronously to see if it's already downloaded
        const downloadChecks = await Promise.all(
          mediaAssets.map(async (asset) => ({
            itemId: asset.itemId,
            itemUrl: asset.itemUrl,
            isDownloaded: await isAlreadyDownloaded(asset.itemId),
          })),
        );

        // Build the filesToDownload object with only non-downloaded files
        const filesToDownload = downloadChecks
          .filter((check) => !check.isDownloaded)
          .reduce((acc, check) => {
            acc[check.itemId] = getCloudfrontUrl(check.itemUrl);
            return acc;
          }, {});

        const fileIds = Object.keys(filesToDownload);

        if (fileIds.length === 0) {
          logger.addLog('No videos to download');
          resolve([]);
          return;
        }

        logger.addLog(`Starting download of ${fileIds.length} videos`);

        let completedDownloads = 0;
        let failedDownloads = 0;
        const downloadResults = [];
        const totalFiles = fileIds.length;

        const checkAllDownloadsComplete = () => {
          if (completedDownloads + failedDownloads === totalFiles) {
            if (failedDownloads > 0) {
              logger.addLog(`Download completed with ${failedDownloads} failures`);
              reject(new Error(`${failedDownloads} videos failed to download`));
            } else {
              logger.addLog('All videos downloaded successfully');
              resolve(downloadResults);
            }
          }
        };

        if (typeof tizen === 'undefined' || !tizen.download) {
          logger.addLog('Error: Tizen DownloadManager API not available');
          reject(new Error('Tizen DownloadManager API not available'));
          return;
        }

        logger.addLog('DownloadManager will create picflow directory automatically');

        fileIds.forEach((fileId) => {
          const videoUrl = filesToDownload[fileId];
          const fileExtension = getFileExtensionFromUrl(videoUrl);
          const fileName = `${fileId}.${fileExtension}`;

          logger.addLog(`Creating download request for ${fileName} from ${videoUrl}`);

          try {
            const downloadRequest = new tizen.DownloadRequest(
              videoUrl,
              'documents',
              `picflow_${fileName}`,
            );

            const downloadId = tizen.download.start(downloadRequest, {
              onprogress: function (id, receivedSize, totalSize) {
                const progress = Math.round((receivedSize / totalSize) * 100);
                logger.addLog(`Download progress for ${fileName}: ${progress}%`);
              },

              onpaused: function (id) {
                logger.addLog(`Download paused: ${fileName}`);
              },

              oncanceled: function (id) {
                logger.addLog(`Download canceled: ${fileName}`);
                failedDownloads++;
                checkAllDownloadsComplete();
              },

              oncompleted: function (id, path) {
                logger.addLog(`Download completed: ${fileName} at ${path}`);

                const localStorageKey = getTizenVideoItemKey(fileId);
                localStorage.setItem(localStorageKey, path);
                logger.addLog(`Saved to localStorage: ${localStorageKey} = ${path}`);

                downloadResults.push({
                  fileId,
                  fileName,
                  path,
                  status: 'completed',
                });
                completedDownloads++;
                checkAllDownloadsComplete();
              },

              onfailed: function (id, error) {
                logger.addLog(`Download failed: ${fileName} - ${error.name}`);
                downloadResults.push({
                  fileId,
                  fileName,
                  error: error.name,
                  status: 'failed',
                });
                failedDownloads++;
                checkAllDownloadsComplete();
              },
            });

            logger.addLog(`Started download for ${fileName} with ID: ${downloadId}`);
          } catch (error) {
            logger.addLog(`Error starting download for ${fileName}: ${error.message}`);
            logger.addLog(
              `URL: ${videoUrl}, Destination: documents, Filename: picflow_${fileName}`,
            );
            downloadResults.push({
              fileId,
              fileName,
              error: error.message,
              status: 'failed',
            });
            failedDownloads++;
            checkAllDownloadsComplete();
          }
        });
      })
      .catch((error) => {
        logger.addLog('Error fetching media assets');
        reject(error);
      });
  });
}
